import { Check, Close, ExpandCircleDown } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { format } from "date-fns";
import { type MouseEvent, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useGetPartnerQuery } from "src/shared/api/partner";
import { ChipComponent } from "src/shared/components/ChipComponent";
import { colorsByPartnersCompany } from "src/shared/constants/partners/colors";
import { AgreementStatusByValue } from "src/shared/constants/partners/constants";
import { useGetErpPartnerActQuery } from "../model/api/api";
import { ReconciliationActModal } from "./ReconciliationActModal";

export const ErpPartnersActs = () => {
  const { partner_id } = useParams();

  const { data: partner, isFetching: isPartnerFetching } = useGetPartnerQuery(
    partner_id ?? skipToken,
  );

  const { data: partnerActs, isFetching: isPartnerActsFetching } =
    useGetErpPartnerActQuery(partner_id ?? skipToken);

  const [isReconciliationModalOpen, setIsReconciliationModalOpen] =
    useState(false);

  const [selectedContractId, setSelectedContractId] = useState<number | null>(
    null,
  );

  const tableHeaders = [
    { id: "created_at", label: "Дата" },
    { id: "id", label: "№" },
    { id: "custom_column", label: "Документ" },
    { id: "act_date", label: "Дата документа" },
    { id: "sum_to_pay", label: "Сумма акта (с НДС)" },
    { id: "paid_date", label: "Дата выплаты" },
    { id: "paid_amount", label: "Выплачено" },
    { id: "is_act_received", label: "Оригинал" },
    { id: "document_link", label: "Документ" },
  ];

  const handleOpenReconciliationModal = (
    event: MouseEvent<HTMLButtonElement>,
    contractId: number,
  ) => {
    event.stopPropagation();

    setSelectedContractId(contractId);
    setIsReconciliationModalOpen(true);
  };

  const handleCloseReconciliationModal = () => {
    setIsReconciliationModalOpen(false);
    setSelectedContractId(null);
  };

  if (isPartnerActsFetching || isPartnerFetching) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const canShowReconciliationAct = new Date().getDate() > 10;

  return (
    <>
      <Stack spacing={2}>
        {partnerActs?.map((contract) => (
          <Box key={contract.id}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandCircleDown />}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    pr: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 3,
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="subtitle1">
                      Договор №{contract.contract_number}
                    </Typography>

                    <Typography color="text.secondary">
                      От:{" "}
                      {format(new Date(contract.contract_date), "dd.MM.yyyy")}
                    </Typography>

                    <Typography>
                      {AgreementStatusByValue[contract.contract_state.value]}
                    </Typography>

                    <ChipComponent
                      label={contract.agency.name}
                      backgroundColor={
                        colorsByPartnersCompany[contract.agency.value]
                      }
                    />

                    <ChipComponent label="КДА" backgroundColor="green" />
                  </Box>

                  {canShowReconciliationAct && (
                    <Button
                      variant="contained"
                      onClick={(event) =>
                        handleOpenReconciliationModal(event, contract.id)
                      }
                    >
                      Акт сверки
                    </Button>
                  )}
                </Box>
              </AccordionSummary>

              <AccordionDetails>
                {contract.acts.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      Нет актов по данному договору
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {tableHeaders.map((header) => (
                            <TableCell
                              key={header.id}
                              sx={{
                                fontWeight: 600,
                                backgroundColor: "grey.100",
                                py: 1.5,
                              }}
                            >
                              {header.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {contract.acts.map((act) => (
                          <TableRow key={act.id}>
                            <TableCell>
                              {format(new Date(act.created_at), "dd.MM.yyyy")}
                            </TableCell>

                            <TableCell>{act.id}</TableCell>

                            <TableCell>Акт</TableCell>

                            <TableCell>
                              {format(new Date(act.act_date), "dd.MM.yyyy")}
                            </TableCell>

                            <TableCell>{act.sum_to_pay}</TableCell>

                            <TableCell>
                              {act.paid_date
                                ? format(new Date(act.paid_date), "dd.MM.yyyy")
                                : "-/-"}
                            </TableCell>

                            <TableCell>{act.paid_amount}</TableCell>

                            <TableCell>
                              {act.is_act_received ? (
                                <Check color="success" />
                              ) : (
                                <Close color="warning" />
                              )}
                            </TableCell>

                            <TableCell>
                              {act.document_link && (
                                <Link
                                  to={act.document_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Документ
                                </Link>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        ))}
      </Stack>

      <ReconciliationActModal
        open={isReconciliationModalOpen}
        partnerId={partner_id!}
        partnerName={partner?.name ?? ""}
        contractId={selectedContractId}
        onClose={handleCloseReconciliationModal}
      />
    </>
  );
};
